import React, { useCallback, useState } from 'react';
import { EstimateBox, Buttons } from './styles';
import Button from '../../button';
import dayjs from 'dayjs';
import { floatToHHMM, getFloatTimeValue, getRawComment, isEmpty } from '../../../helpers/common';
import isString from 'lodash/isString';
import TimePickerModal from '../../timePicker';
import Dropdown from '../../dropdown/mainDropdown';
import { TaskModel } from '../../../mobx/models/task';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, CHIP_SIZE, CHIP_TYPE, COMMENT_TYPE } from '../../../global/constants';
import { nanoid } from 'nanoid';
import { RawUpdateCommentInterface } from '../../../interfaces/TaskInterface';
import { updateTaskDetails , createNewComment } from '../../../services/taskServices';
import { TASK_ANALYTICS } from '../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import Chip from '../../chipComponent';

interface Props {
  isCreateTask?: boolean;
  taskData?: TaskModel;
  isTaskDetails?: boolean;
}

const EstimateDropdown: React.FC<Props> = (props) => {
  // Props declaration
  const { isTaskDetails = false, taskData, isCreateTask = false } = props;

  //Mobx store variables
  const { taskStore, currentUser } = rootStore;
  const { createTaskInput } = taskStore;

  //State
  const [selectedTime, setSelectedTime] = useState(floatToHHMM(createTaskInput?.estimate || 0) || '00:00');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  /**
   * Gets the appropriate title for the estimate dropdown
   * @returns The title to display
   */
  const getEstimateTitle = useCallback(() => {
    if (isTaskDetails) {
      if (!isEmpty(taskData?.estimate)) {
        if (isString(taskData?.estimate) && taskData?.estimate !== 0) {
          return taskData?.estimate;
        }
        if (
          !isString(taskData?.estimate) &&
          floatToHHMM(taskData?.estimate || 0) !== '00:00' &&
          taskData?.estimate !== 0
        ) {
          return floatToHHMM(taskData?.estimate || 0);
        }
      }
      return 'Set Time';
    }

    if (
      !isEmpty(createTaskInput?.estimate) &&
      createTaskInput?.estimate !== 0 &&
      floatToHHMM(createTaskInput?.estimate || 0) !== '00:00'
    ) {
      return isString(createTaskInput?.estimate)
        ? createTaskInput?.estimate
        : floatToHHMM(createTaskInput?.estimate || 0);
    }

    return '';
  }, [isTaskDetails, taskData?.estimate, createTaskInput?.estimate]);

  /**
   * Handles time picker value changes
   * @param value - Selected time value
   */
  const handleTimeChange = (value: string) => {
    setSelectedTime(value);
  };

  /**
   * Handles setting the estimate time for a task
   * Creates appropriate comments and updates task details
   * @param time - Selected time in HH:MM format
   */
  const onclickSet = useCallback(
    async (time: string) => {
      if (isCreateTask) {
        createTaskInput.setEstimate(getFloatTimeValue(time));
      } else {
        if (!currentUser) return;
        taskData?.setEstimate(getFloatTimeValue(time));
        const newComment: RawUpdateCommentInterface = {
          id: nanoid(),
          type: COMMENT_TYPE.ESTIMATE_TIME,
          estimateTime: getFloatTimeValue(time)
        };
        const updatedComment = getRawComment(newComment, currentUser);
        taskData?.upsertComment(updatedComment);
        const payloadTask = { estimateTime: getFloatTimeValue(time || ''), projectId: taskData?.projectId };
        const result = await updateTaskDetails(taskData?.id, payloadTask);
        const commentResult = await createNewComment(taskData?.id, {
          Type: COMMENT_TYPE.ESTIMATE_TIME,
          EstimateTime: getFloatTimeValue(time || '')
        });
        if (result && commentResult) {
          trackAnalyticActivity(TASK_ANALYTICS.ESTIMATION_SET);
        }
      }
    },
    [isCreateTask, createTaskInput, currentUser, taskData]
  );

  return (
    <Dropdown
      isMinWidth={198}
      isRemoveMaxHeight={true}
      content={
        <Chip
          size={isCreateTask ? CHIP_SIZE.SMALL : CHIP_SIZE.MEDIUM}
          variant={isCreateTask ? CHIP_TYPE.FILL : CHIP_TYPE.EMPTY}
          title={getEstimateTitle()}
          isActive={isDropdownOpen}
          leadingIconName='navbar-TimeTracking-icon'
        />
      }
      trigger='click'
      modalTitle='Estimation'
      updateDropdownOpen={(isOpen) => setIsDropdownOpen(isOpen)}>
      <EstimateBox>
        <TimePickerModal
          label='Duration (HH:MM)'
          value={dayjs(selectedTime, 'HH:mm')}
          onSelect={(value) => handleTimeChange(value.format('HH:mm'))}
        />
        <Buttons>
          <Button title='Set' variant={BUTTON_TYPE.PRIMARY} onClick={() => onclickSet(selectedTime)} />
          <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} />
        </Buttons>
      </EstimateBox>
    </Dropdown>
  );
};

export default observer(EstimateDropdown);
